import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import type { CompressionResult } from '../utils/imageCompressor';
import { formatBytes } from '../utils/constants';
import { useTranslation } from '../i18n/LanguageContext';

interface CompressionStatsProps {
  results: CompressionResult[];
}

export default function CompressionStats({ results }: CompressionStatsProps) {
  const { t } = useTranslation();
  const [showChart, setShowChart] = useState(false);

  const compressed = results.filter((r) => !r.skipped);
  const skipped = results.filter((r) => r.skipped);
  const totalOriginal = results.reduce((s, r) => s + r.originalSize, 0);
  const totalCompressed = results.reduce((s, r) => s + r.compressedSize, 0);
  const savedPercent = totalOriginal > 0 ? Math.round((1 - totalCompressed / totalOriginal) * 100) : 0;

  const chartData = compressed.slice(0, 30).map((r) => ({
    name: r.path.split('/').pop() || r.path,
    [t.chartOriginal]: Math.round(r.originalSize / 1024),
    [t.chartCompressed]: Math.round(r.compressedSize / 1024),
  }));

  return (
    <div className="stats">
      <h2>{t.statsTitle}</h2>

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-value">{compressed.length}</span>
          <span className="stat-label">{t.filesCompressed}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{skipped.length}</span>
          <span className="stat-label">{t.filesSkipped}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{formatBytes(totalOriginal)}</span>
          <span className="stat-label">{t.sizeBefore}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{formatBytes(totalCompressed)}</span>
          <span className="stat-label">{t.sizeAfter}</span>
        </div>
        <div className="stat-card stat-card-accent">
          <span className="stat-value">{savedPercent}%</span>
          <span className="stat-label">{t.compression}</span>
        </div>
      </div>

      {chartData.length > 0 && (
        <>
          <button
            className="toggle-chart-btn"
            onClick={() => setShowChart(!showChart)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ transform: showChart ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
            {showChart ? t.hideFileStats : t.showFileStats}
          </button>

          {showChart && (
            <div className="chart-container">
              <h3>{t.chartTitle}</h3>
              <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 30)}>
                <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey={t.chartOriginal} fill="#94a3b8" />
                  <Bar dataKey={t.chartCompressed} fill="#2563EB" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
